import type { EpcDepartmentMaster } from '../../../../services/epcDepartmentMasterService';
import { masterId } from '../../../../services/epcDepartmentMasterService';
import type { EpcProjectOrgUnit } from '../../../../services/epcProjectService';

export type MasterAvailability = {
  master: EpcDepartmentMaster;
  /** True when an ACTIVE org unit already instantiates this master (legacy rows: name match). */
  alreadyAdded: boolean;
};

export function computeMasterAvailability(
  masters: readonly EpcDepartmentMaster[],
  orgUnits: readonly EpcProjectOrgUnit[],
): MasterAvailability[] {
  const active = orgUnits.filter((u) => !u.inactive);
  const linkedIds = new Set(active.map((u) => u.epcDepartmentMasterId).filter(Boolean) as string[]);
  const activeNames = new Set(active.map((u) => (u.name || '').trim().toLowerCase()));
  return masters.map((m) => ({
    master: m,
    alreadyAdded: linkedIds.has(masterId(m)) || activeNames.has(m.name.trim().toLowerCase()),
  }));
}

export function toggleTemplateSelection(selected: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(selected);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

export function parseFocusAreas(raw: string): string[] {
  return raw
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 24);
}

export type DeptCreateSpec = {
  masterId: string;
  name: string;
  focusAreas: string[];
};

/** Selected master ids → ordered list of org units to create. */
export function buildCreateSpecs(
  selected: ReadonlySet<string>,
  masters: readonly EpcDepartmentMaster[],
): DeptCreateSpec[] {
  return masters
    .filter((m) => selected.has(masterId(m)))
    .map((m) => ({ masterId: masterId(m), name: m.name, focusAreas: [...(m.focusAreas ?? [])] }));
}

export type DeptCreateOutcome =
  | { spec: DeptCreateSpec; ok: true; orgUnitId: string }
  | { spec: DeptCreateSpec; ok: false; error: string };

export function partitionCreateOutcomes(outcomes: readonly DeptCreateOutcome[]): {
  created: Array<{ masterId: string; name: string; orgUnitId: string }>;
  failed: Array<{ masterId: string; name: string; error: string }>;
} {
  const created: Array<{ masterId: string; name: string; orgUnitId: string }> = [];
  const failed: Array<{ masterId: string; name: string; error: string }> = [];
  for (const o of outcomes) {
    /* `o.ok === false` (not `else`): with this repo's `strictNullChecks: false`,
       the else-branch of a truthiness check does not narrow the union. */
    if (o.ok) created.push({ masterId: o.spec.masterId, name: o.spec.name, orgUnitId: o.orgUnitId });
    if (o.ok === false) failed.push({ masterId: o.spec.masterId, name: o.spec.name, error: o.error });
  }
  return { created, failed };
}
